import { z } from 'zod';
import { DepositStatusSchema, EventIdSchema, MoneySchema, PaginationQuerySchema, TransactionIdSchema, WithdrawalIdSchema, } from './common.js';
import { AmountAdjustmentSchema, CheckMatrixSchema, CreditSettlementSchema, } from './verification.js';
/**
 * `GET /v1/transactions/{id}`, `GET /v1/transactions`, `POST .../mark-paid`,
 * `POST .../credit-result`, `POST .../release` (SAD §8, M-01).
 */
/** Path parameters for the transaction-scoped endpoints. */
export const TransactionIdParamSchema = z
    .object({
    id: TransactionIdSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// GET /v1/transactions/{id} — full state + verification + event timeline
// ---------------------------------------------------------------------------------------------
/**
 * The `verification` block on `GET /v1/transactions/{id}` (SAD §8):
 *
 * ```
 * "verification":{ "provider":"slipok", "trans_ref":"01420064...",
 *   "checks":{ "name":"PASS","amount":"PASS","bank":"PASS","duplicate":"PASS" } }
 * ```
 *
 * Present once a slip has been verified (VERIFYING onward); absent before that.
 */
export const TransactionVerificationSchema = z
    .object({
    provider: z.string().min(1),
    trans_ref: z.string().min(1),
    checks: CheckMatrixSchema,
    /**
     * Present when the amount check failed but policy auto-reconciled the difference — the same
     * value `slip.verified` carried as `adjustment` and `transaction.confirmed` carried as
     * `withdrawer_adjustment`. On the read model so a recovery that replays this feed (§13's
     * deposit twin) settles the same figures the lost webhook instructed, instead of the
     * reserved amount. Additive and optional: absent on every exact-match slip.
     */
    adjustment: AmountAdjustmentSchema.optional(),
})
    .strict();
/** One row of a transaction's event timeline, as returned by `GET /v1/transactions/{id}`. */
export const TransactionTimelineEntrySchema = z
    .object({
    id: EventIdSchema,
    /**
     * Not pinned to the SAD §9 catalog: the participant plane filters to the catalog before
     * serializing, but the operator plane shows the whole stream — including internal
     * transitions like `review.approved`, which is the only record of *who moved the money*.
     * The timeline is a display of the event log, and the log records more than the catalog.
     */
    type: z.string().min(1),
    sequence: z.number().int().nonnegative(),
    /**
     * Operator plane only, and only for human actions: who pressed the button. `label` is the
     * operator's username, falling back to the raw id when the account no longer resolves.
     * Absent for SYSTEM/TIMER/PARTICIPANT actors and on participant responses.
     */
    actor: z
        .object({ type: z.literal('OPERATOR'), label: z.string().min(1) })
        .strict()
        .nullable()
        .optional(),
    created_at: z.string().datetime({ offset: true }),
})
    .strict();
/** `200` response for `GET /v1/transactions/{id}` — full state + verification + event timeline. */
export const TransactionDetailSchema = z
    .object({
    id: TransactionIdSchema,
    status: DepositStatusSchema,
    client_reference: z.string().min(1),
    amount: MoneySchema,
    matched_withdrawal_id: WithdrawalIdSchema.nullable(),
    /**
     * Operator plane only — the two websites the match pairs: `participant` owns this deposit,
     * `withdrawal_participant` owns the matched withdrawal.
     *
     * A participant reading its own transaction already knows whose it is, which is why neither
     * key appears on that plane. An operator does not: since cross-website matching a `web_a`
     * deposit can settle a `web_b` withdrawal, and the detail page had no way to say so — it
     * named one side and left the reader to assume the other. `withdrawal_participant` is null
     * whenever `matched_withdrawal_id` is (and on a match whose withdrawal has since been read
     * back as missing); equal to `participant` on an ordinary same-website match.
     *
     * Both optional on the wire so a console deployed ahead of the API keeps parsing.
     */
    participant: z.string().min(1).optional(),
    withdrawal_participant: z.string().min(1).nullable().optional(),
    /**
     * Operator plane only — absent on the participant response. The depositor's declared name
     * (verify-the-sender `payer_account`); null when the reservation carried none.
     */
    depositor_name: z.string().nullable().optional(),
    /** Operator plane only — the matched withdrawal's registered payee name. */
    withdrawer_name: z.string().nullable().optional(),
    /**
     * Operator plane only — the two customers as the participating website identifies them:
     * `…_account_ref` is the opaque identity, `…_account_display` the username it shows them
     * under (`account_directory`, latest write wins). A display is null until the website sends
     * one; the withdrawer half is null when nothing is matched. All optional on the wire so a
     * console deployed ahead of the API renders the refs rather than failing the page.
     */
    depositor_account_ref: z.string().min(1).optional(),
    depositor_account_display: z.string().min(1).nullable().optional(),
    withdrawer_account_ref: z.string().min(1).nullable().optional(),
    withdrawer_account_display: z.string().min(1).nullable().optional(),
    /**
     * Operator plane only — username behind the transaction's most recent human action, null
     * when its whole life was automatic. The facts-panel twin of the timeline's actor chips.
     */
    operator_name: z.string().nullable().optional(),
    evidence_attempts: z.number().int().nonnegative(),
    deadline_at: z.string().datetime({ offset: true }).nullable(),
    /**
     * True when the transaction was completed by an `EXTERNAL` review decision — settled
     * manually outside the credit rails, with no PaymentFlow credit instruction ever issued.
     * The website's recon backstop must skip crediting these: the money already moved by hand,
     * and enqueueing a credit from this read model would pay the depositor twice. Additive and
     * optional; absent means a normal credit-rail completion.
     */
    settled_externally: z.boolean().optional(),
    /**
     * What the website's ledger actually moved, when the credit carried an amount adjustment —
     * the same record `credit-result` reported and `transaction.completed` echoed. The outcome
     * half of the reconciliation, where `verification.adjustment` is the instruction half: the
     * adjustment says "pay 60 of the 100 reserved", this says the depositor was credited 60.00
     * and 40.00 of the funds lock went back to the withdrawer.
     *
     * On the read model because the amounts are otherwise invisible outside the event payload —
     * `amount` is the *reserved* figure and stays 100.00 forever, which is what makes an
     * auto-reconciled deposit read as "approved on a mismatched amount". Additive and optional:
     * absent on every exact-match credit and on anything not yet completed.
     */
    settlement: CreditSettlementSchema.optional(),
    /**
     * Operator plane only — how an `EXPIRED` transaction came to expire: `PARTICIPANT_RELEASE`
     * (the depositor pressed cancel and handed the slot back), `RESERVATION_TTL` (the timer
     * reclaimed it) or `OPERATOR_CANCELLED` (an admin cancelled the matched withdrawal out from
     * under a reservation that had never been paid). Read off the `transaction.expired` event's
     * `cause`; the console shows a release as *cancelled by the customer* and an operator cancel
     * as *cancelled by an admin*, matching the History list. Absent on non-EXPIRED transactions,
     * on participant responses, and on events recorded before the cause existed.
     */
    expired_cause: z
        .enum(['RESERVATION_TTL', 'PARTICIPANT_RELEASE', 'OPERATOR_CANCELLED'])
        .optional(),
    verification: TransactionVerificationSchema.nullable(),
    events: z.array(TransactionTimelineEntrySchema),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// GET /v1/transactions — keyset reconciliation feed
// ---------------------------------------------------------------------------------------------
/** Query parameters for `GET /v1/transactions` (SAD §8: `?status=&updated_since=&cursor=`). */
export const TransactionsQuerySchema = PaginationQuerySchema.extend({
    status: DepositStatusSchema.optional(),
}).strict();
/**
 * One row of the reconciliation feed — lighter than the full detail view (no event timeline),
 * matching the recon use case of "what changed since `updated_since`".
 */
export const TransactionReconItemSchema = z
    .object({
    id: TransactionIdSchema,
    status: DepositStatusSchema,
    client_reference: z.string().min(1),
    amount: MoneySchema,
    matched_withdrawal_id: WithdrawalIdSchema.nullable(),
    /**
     * The amount auto-reconciliation instruction the paying credit carried, named exactly as
     * `transaction.confirmed` names it. A website recovering a lost confirmed webhook from this
     * feed (§13's deposit twin) must honour it — enqueueing the credit from `amount` alone would
     * credit the reserved figure for an under-paid slip and swallow the withdrawer's refund.
     * Additive and optional: absent whenever the transaction's latest verified slip carried no
     * adjustment.
     */
    withdrawer_adjustment: AmountAdjustmentSchema.optional(),
    /**
     * True when the transaction was completed by an `EXTERNAL` review decision — settled
     * manually outside the credit rails. **Recon must not enqueue a credit when this is true**:
     * no credit instruction was ever issued for this deposit, the money moved by hand, and a
     * recovery that replays this feed as §13's deposit twin would otherwise credit the
     * depositor a second time. Additive and optional; absent means a normal credit-rail
     * completion.
     */
    settled_externally: z.boolean().optional(),
    /**
     * True when the matched withdrawal belongs to ANOTHER website (clearing) — named exactly as
     * `transaction.confirmed` names it, because this feed is that webhook's recovery path. A
     * website replaying a cross-website credit from here must take the credit-only path: no
     * local lock resolution, no slice materialization, no withdrawer bookkeeping — including
     * when `withdrawer_adjustment` rides along (cross-website reconcile: credit `paid_amount`,
     * the lock side is the other website's, instructed on ITS `withdrawal.fulfilled`).
     * Additive and optional; absent means same-website, every row before clearing existed.
     */
    cross_website: z.literal(true).optional(),
    updated_at: z.string().datetime({ offset: true }),
})
    .strict();
/** `200` response for `GET /v1/transactions`. */
export const TransactionsListResponseSchema = z
    .object({
    items: z.array(TransactionReconItemSchema),
    next_cursor: z.string().nullable(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// POST /v1/transactions/{id}/mark-paid
// ---------------------------------------------------------------------------------------------
/**
 * `POST /v1/transactions/{id}/mark-paid` takes no body (SAD §8) — RESERVED → AWAITING_EVIDENCE;
 * starts the evidence deadline; withdrawal → LOCKED. `409` unless RESERVED.
 */
export const MarkPaidRequestSchema = z.object({}).strict();
/** `200` response for `POST /v1/transactions/{id}/mark-paid`. */
export const MarkPaidResponseSchema = z
    .object({
    id: TransactionIdSchema,
    status: z.literal('AWAITING_EVIDENCE'),
    deadline_at: z.string().datetime({ offset: true }),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// POST /v1/transactions/{id}/credit-result — authoritative money ACK (SAD §9 two-phase)
// ---------------------------------------------------------------------------------------------
/**
 * Request body for `POST /v1/transactions/{id}/credit-result` (SAD §8, §9):
 * ```
 * {"result":"SUCCESS|FAILED","ledger_entry_ref":"B-CR-9987"}
 * ```
 * The authoritative, idempotent (by `ledger_entry_ref`) money acknowledgement — a webhook is
 * only a notification (SAD §9).
 */
export const CreditResultRequestSchema = z
    .object({
    result: z.enum(['SUCCESS', 'FAILED']),
    ledger_entry_ref: z.string().min(1).max(255),
    /**
     * What the ledger actually moved, when the credit instruction carried a
     * `withdrawer_adjustment` (amount auto-reconciliation). This report — not the instruction —
     * is the authoritative record of the amounts: for an overpayment only the website knows how
     * much of the extra its available balance could cover. Additive and optional; omitted on
     * every exact-match credit.
     */
    settlement: CreditSettlementSchema.optional(),
})
    .strict();
/** `200` response for `POST /v1/transactions/{id}/credit-result`. */
export const CreditResultResponseSchema = z
    .object({
    id: TransactionIdSchema,
    status: DepositStatusSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// POST /v1/transactions/{id}/release
// ---------------------------------------------------------------------------------------------
/**
 * `POST /v1/transactions/{id}/release` takes no body (SAD §8) — RESERVED only → EXPIRED, release
 * slot. `409` once marked paid (from then on, only review can resolve it).
 */
export const ReleaseTransactionRequestSchema = z.object({}).strict();
/** `200` response for `POST /v1/transactions/{id}/release`. */
export const ReleaseTransactionResponseSchema = z
    .object({
    id: TransactionIdSchema,
    status: z.literal('EXPIRED'),
})
    .strict();
//# sourceMappingURL=transactions.js.map